"use client";

// ─────────────────────────────────────────────────────────────────────────
// The card-first trial wall (PANDA-2341).
//
// Opens on the FIRST gated action — the user has typed their idea, signed up,
// come back to the builder with the prompt intact, and pressed Build. This is
// the moment the ask lands against a stated intent rather than a blank screen,
// which is why the wall sits here and not at sign-in.
//
// It renders ONE server decision (`reason: "card_required"` from
// /api/credits/check) and never forms its own view of whether the user is
// gated. Every price and duration on it comes from lib/trial/config.ts via
// `terms`, so the modal can never contradict what the processor charges.
//
// The visual is the approved design mock, value for value — Space Grotesk for
// display type, Inter for body, the PayPal-yellow pill. The mock IS the dark
// theme; light theme is the same layout on the inverted palette (CF_TOKENS).
//
// Distinct from components/trial/trial-offer-modal.tsx, which is the OPPORTUNISTIC
// offer shown at credit exhaustion and eight other surfaces. That one can be
// dismissed and the user keeps building; this one is the door. They are kept
// apart deliberately: folding a dismissible upsell and a hard wall into one
// component is how a wall acquires a quiet way around it.
// ─────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { cn } from "@/lib/utils";
import { MODAL_VIEWPORT_FRAME_SM } from "@/components/ui/modal-surface";

export interface CardFirstTrialTerms {
  days: number;
  credits: number;
  /** "Free" — what is captured today, as a label. */
  priceLabel: string;
  /** "$29" — what is charged when the trial converts. */
  convertPriceLabel: string;
  /** The full negative-option disclosure sentence. */
  termsLine: string;
}

interface CardFirstTrialModalProps {
  open: boolean;
  terms: CardFirstTrialTerms;
  /** Starts checkout. Resolves only if the redirect did NOT happen. */
  onStart: () => Promise<void>;
  /**
   * Close. The wall is dismissible ON PURPOSE — the ticket requires that a
   * blocked action never traps navigation. Dismissing returns the user to their
   * project; it does not let the build through.
   */
  onClose: () => void;
}

// ⚠️ PLACEHOLDER social proof, copied verbatim from the approved design mock.
// Neither the builder count nor the quote is backed by data in this repo —
// same status as HOME_TESTIMONIALS in app/home-client.tsx. Replace both with a
// real count and a real, consented customer quote before this ships to
// everyone, or set SHOW_CARD_FIRST_SOCIAL_PROOF to false to hide them (the
// eyebrow then falls back to a claimless line and the quote block is omitted).
const SHOW_CARD_FIRST_SOCIAL_PROOF: boolean = true;
const CARD_FIRST_BUILDER_COUNT = "9,000+";
const CARD_FIRST_TESTIMONIAL = {
  quote: "I had my idea built and live that afternoon.",
  name: "Terry Yoell",
  role: "CEO, Flock Mobility",
} as const;

// The trial start route only takes a $0 trial through PayPal (TRIAL_PRICE_CENTS
// is 0), so the PayPal mark is a true statement of where the button goes. On
// any other provider the button keeps its words and drops the mark.
const CHECKOUT_IS_PAYPAL = process.env.NEXT_PUBLIC_PAYMENT_PROVIDER === "paypal";

// Palette. Dark values are the mock's; light values invert it, with the green
// darkened wherever it carries text so it stays above 4.5:1 on white.
const CF_TOKENS = [
  "[--cf-bg:#FFFFFF] dark:[--cf-bg:#0E100E]",
  "[--cf-side:#F7F7F4] dark:[--cf-side:#0B0D0B]",
  "[--cf-box:#FAFAF8] dark:[--cf-box:#151815]",
  "[--cf-line:rgba(14,16,14,0.10)] dark:[--cf-line:rgba(255,252,248,0.10)]",
  "[--cf-ink:#0E100E] dark:[--cf-ink:#F5F3EF]",
  "[--cf-ink2:#5F5C56] dark:[--cf-ink2:#95918A]",
  "[--cf-ink3:#8A867F] dark:[--cf-ink3:#5F5C56]",
  "[--cf-accent:#0F7A4B] dark:[--cf-accent:#4BC48C]",
  "[--cf-check:#1E9E63] dark:[--cf-check:#4BC48C]",
  "[--cf-face:#ECEBE8] dark:[--cf-face:#252824]",
].join(" ");

const DISPLAY = "[font-family:var(--font-space-grotesk),ui-sans-serif,system-ui,sans-serif]";
const BODY = "[font-family:var(--font-sans),ui-sans-serif,system-ui,sans-serif]";
const LINE = "border-[var(--cf-line)]";

/**
 * What the trial unlocks, in the order the user will do it — the founder's
 * journey (build → edit → launch → get users) followed by the two terms.
 * "Start getting users" is emphasised: it is the outcome the rest exist for.
 */
function trialBenefits(days: number): readonly { label: string; emphasis?: boolean }[] {
  return [
    { label: "Build your app" },
    { label: "Edit and improve it with AI" },
    { label: "Launch to a live URL" },
    { label: "Start getting users", emphasis: true },
    { label: `Full Panda Pro access for ${days} days` },
    { label: "Cancel any time" },
  ];
}

const REASSURANCES: readonly { label: string; emphasis?: boolean }[] = [
  { label: "Nothing charged today", emphasis: true },
  { label: "We’ll remind you before your first payment" },
  { label: "Cancel any time" },
];

function Bullets({
  items,
  className,
}: {
  items: readonly { label: string; emphasis?: boolean }[];
  className?: string;
}) {
  return (
    <ul className={cn("grid", className)}>
      {items.map((item) => (
        <li
          key={item.label}
          className="flex items-start gap-2 text-[11.5px] leading-[1.4] text-[var(--cf-ink2)] lg:gap-2.5 lg:text-[14px]"
        >
          <Check
            strokeWidth={3}
            className="mt-0.5 size-[13px] flex-none text-[var(--cf-check)] lg:size-[15px]"
          />
          <span className={item.emphasis ? "font-medium text-[var(--cf-ink)]" : undefined}>
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PayPalMark() {
  return (
    <span
      className={cn(
        DISPLAY,
        "inline-flex items-center text-[15px] font-bold italic tracking-[-0.02em] lg:text-[19px]",
      )}
    >
      <svg
        viewBox="0 0 24 28"
        aria-hidden="true"
        className="mr-[5px] h-[14px] w-[12px] lg:h-[17px] lg:w-[15px]"
      >
        <path
          fill="#003087"
          d="M8.6 26.4H4.2a.6.6 0 0 1-.6-.7L7.2 2.3a.9.9 0 0 1 .9-.8h8.3c3.9 0 6.5 1.8 6.1 5.7-.4 4.3-3.6 6.8-8 6.8h-3.5a.9.9 0 0 0-.9.8l-1 6.1z"
        />
        <path
          fill="#009CDE"
          d="M12.4 27.5H8a.6.6 0 0 1-.6-.7l.6-3.7a.9.9 0 0 1 .9-.8h3.2c4.4 0 7.6-2.5 8-6.8.1-1.2-.1-2.2-.6-3 1.9.9 2.9 2.7 2.6 5.5-.5 4.7-3.9 7.4-8.6 7.4h-.2a.9.9 0 0 0-.9.8v1.3z"
        />
      </svg>
      <span className="text-[#003087]">Pay</span>
      <span className="text-[#009CDE]">Pal</span>
    </span>
  );
}

const CARD_MARKS: readonly { label: string; bg: string }[] = [
  { label: "VISA", bg: "bg-[#1434CB]" },
  { label: "MC", bg: "bg-[#EB001B]" },
  { label: "AMEX", bg: "bg-[#0077A6]" },
  { label: "DISC", bg: "bg-[#F68B1F]" },
];

function initials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function CardFirstTrialModal({ open, terms, onStart, onClose }: CardFirstTrialModalProps) {
  const [starting, setStarting] = useState(false);

  async function handleStart() {
    setStarting(true);
    try {
      await onStart();
    } finally {
      // A successful start is a full-page redirect and this component unmounts
      // with the document. Reaching here means the redirect did NOT happen, so
      // the button must come back — a wall whose only control is permanently
      // disabled is a dead end, which is exactly the failure PANDA-2342 logs.
      setStarting(false);
    }
  }

  const todayPrice = terms.priceLabel === "Free" ? "$0" : terms.priceLabel;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-black/60 supports-backdrop-filter:backdrop-blur-sm dark:bg-black/80",
            "data-open:animate-in data-open:fade-in-0",
            "data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          data-testid="card-first-trial-modal"
          className={cn(
            // Phones: full-bleed sheet. ≥sm: centred card capped to the dynamic
            // viewport so browser chrome can never push the CTA off screen.
            "fixed inset-0 z-[100] flex flex-col overflow-hidden sm:rounded-[18px]",
            "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100%-2rem)] sm:max-w-[920px] sm:-translate-x-1/2 sm:-translate-y-1/2",
            MODAL_VIEWPORT_FRAME_SM,
            "data-open:animate-in data-open:fade-in-0 sm:data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 sm:data-closed:zoom-out-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Start your {terms.days}-day free trial
          </DialogPrimitive.Title>

          <div
            className={cn(
              CF_TOKENS,
              BODY,
              LINE,
              // `normal`, not the app's 1.5: the mock's unset text inherits the
              // browser default, and 1.5 visibly inflates the price block.
              "relative flex h-full w-full flex-col overflow-y-auto overscroll-contain bg-[var(--cf-bg)] leading-[normal] text-[var(--cf-ink)] antialiased",
              "sm:h-auto sm:rounded-[18px] sm:border sm:shadow-2xl",
            )}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={cn(
                "absolute right-3.5 top-3.5 z-10 grid size-6 place-items-center rounded-full",
                "text-[var(--cf-ink3)] opacity-45 transition hover:text-[var(--cf-ink)] hover:opacity-100",
                // 24px is the drawn size; the hit area is 40px.
                "after:absolute after:-inset-2 after:content-['']",
              )}
            >
              <X strokeWidth={2.2} className="size-[13px]" />
            </button>

            <div className="grid sm:grid-cols-2">
              {/* ── Left: what they get ───────────────────────────────── */}
              <div className="flex flex-col px-[22px] py-6 sm:pr-4 lg:py-[38px] lg:pl-10 lg:pr-[30px]">
                <p className="mb-3 text-[14px] font-semibold uppercase leading-[1.45] text-[var(--cf-accent)] lg:mb-4 lg:text-[18px] lg:tracking-[0.01em]">
                  {SHOW_CARD_FIRST_SOCIAL_PROOF
                    ? `Join ${CARD_FIRST_BUILDER_COUNT} entrepreneurs building with Panda`
                    : "Build with Panda"}
                </p>

                <h2
                  className={cn(
                    DISPLAY,
                    "text-[27px] font-bold uppercase leading-[0.98] tracking-[-0.035em] lg:text-[38px]",
                  )}
                >
                  Build.
                  <br />
                  Launch.
                  <br />
                  Start free.
                </h2>

                <p className="mt-2.5 max-w-[30ch] text-[12px] leading-[1.5] text-[var(--cf-ink2)] lg:mt-3.5 lg:text-[14.5px]">
                  Build and launch your app with full Panda Pro access for {terms.days} days.
                </p>

                <Bullets
                  items={trialBenefits(terms.days)}
                  className="mt-3.5 gap-[7px] lg:mt-5 lg:gap-[9px]"
                />

                {SHOW_CARD_FIRST_SOCIAL_PROOF && (
                  <figure className={cn(LINE, "mt-6 border-t pt-4 lg:pt-[22px]")}>
                    <blockquote className="text-[12.5px] leading-[1.5] text-[var(--cf-ink)] lg:text-[15px]">
                      &ldquo;{CARD_FIRST_TESTIMONIAL.quote}&rdquo;
                    </blockquote>
                    <figcaption className="mt-[9px] flex items-center gap-[9px]">
                      <span
                        className={cn(
                          DISPLAY,
                          "grid size-[26px] flex-none place-items-center rounded-full bg-[var(--cf-face)] text-[10px] font-semibold text-[var(--cf-ink3)]",
                        )}
                      >
                        {initials(CARD_FIRST_TESTIMONIAL.name)}
                      </span>
                      <span className="text-[10px] leading-[1.3] text-[var(--cf-ink3)] lg:text-[11.5px]">
                        <span className="block text-[11px] font-medium text-[var(--cf-ink)] lg:text-[12.5px]">
                          {CARD_FIRST_TESTIMONIAL.name}
                        </span>
                        {CARD_FIRST_TESTIMONIAL.role}
                      </span>
                    </figcaption>
                  </figure>
                )}
              </div>

              {/* ── Right: the ask ────────────────────────────────────── */}
              <div
                className={cn(
                  LINE,
                  "flex flex-col border-t bg-[var(--cf-side)] px-[22px] py-6",
                  "sm:border-l sm:border-t-0 sm:pl-4 lg:pb-[38px] lg:pl-[30px] lg:pr-10 lg:pt-[58px]",
                )}
              >
                <h3
                  className={cn(
                    DISPLAY,
                    "mb-[13px] pr-[26px] text-[15px] font-bold uppercase leading-[1.15] tracking-[-0.02em] lg:mb-[18px] lg:text-[20px]",
                  )}
                >
                  Start your {terms.days}-day free trial
                </h3>

                {/* Price block. "$0 today" leads because it is the only number
                    charged today; the conversion price sits under a rule so the
                    two are never read as one figure. */}
                <div
                  className={cn(
                    LINE,
                    "rounded-xl border bg-[var(--cf-box)] px-[13px] py-3 lg:px-[17px] lg:py-4",
                  )}
                >
                  <p className="flex items-baseline gap-[9px]">
                    <span
                      className={cn(
                        DISPLAY,
                        "text-[32px] font-bold leading-none tracking-[-0.04em] lg:text-[44px]",
                      )}
                    >
                      {todayPrice}
                    </span>
                    <span className="text-[11.5px] text-[var(--cf-ink2)] lg:text-[14px]">today</span>
                  </p>
                  <p className="mt-1.5 text-[11px] text-[var(--cf-ink2)] lg:text-[14px]">
                    {terms.days} days free
                  </p>
                  <p
                    className={cn(
                      LINE,
                      "mt-[9px] border-t pt-[9px] text-[11.5px] text-[var(--cf-ink2)] lg:mt-[11px] lg:pt-[11px] lg:text-[13.5px]",
                    )}
                  >
                    Then {terms.convertPriceLabel}/month
                  </p>
                </div>

                <button
                  type="button"
                  data-testid="card-first-start"
                  onClick={handleStart}
                  disabled={starting}
                  aria-busy={starting}
                  className={cn(
                    "mt-[13px] flex w-full items-center justify-center gap-[9px] rounded-full bg-[#FFC439]",
                    "px-3.5 py-3 lg:mt-4 lg:px-5 lg:py-[15px]",
                    "transition-[filter,transform] duration-150 hover:brightness-[0.96] active:translate-y-px",
                    // Full opacity while busy: "Redirecting…" is status the user
                    // needs to read.
                    "disabled:cursor-wait disabled:opacity-100",
                  )}
                >
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.02em] text-[#0A1C4A] lg:text-[13px]">
                    {starting ? "Redirecting to checkout…" : "Start my free trial"}
                    {!starting && CHECKOUT_IS_PAYPAL ? " with" : ""}
                  </span>
                  {starting ? (
                    <Loader2 className="size-4 animate-spin text-[#0A1C4A]" />
                  ) : (
                    CHECKOUT_IS_PAYPAL && <PayPalMark />
                  )}
                </button>

                <div className="mt-[9px] flex flex-wrap items-center gap-x-2 gap-y-[5px] lg:mt-[11px] lg:gap-x-2.5 lg:gap-y-[7px]">
                  <span className="flex gap-1" aria-hidden="true">
                    {CARD_MARKS.map((m) => (
                      <b
                        key={m.label}
                        className={cn(
                          m.bg,
                          "grid h-[13px] w-5 place-items-center rounded-[3px] text-[5.5px] font-bold text-white lg:h-[15px] lg:w-6 lg:text-[6.5px]",
                        )}
                      >
                        {m.label}
                      </b>
                    ))}
                  </span>
                  <span className="text-[10px] text-[var(--cf-ink3)] lg:text-[11.5px]">
                    Cards and bank transfer accepted
                  </span>
                </div>

                <Bullets items={REASSURANCES} className="mt-4 gap-[7px]" />

                {/* The negative-option disclosure: amount, when, and the exit —
                    every figure from the offer config. */}
                <p className="mt-[11px] text-[9.5px] leading-[1.55] text-[var(--cf-ink2)] opacity-[0.72] lg:mt-3.5 lg:text-[12px]">
                  Panda Pro starts at {terms.convertPriceLabel}/month after {terms.days} days
                  unless you cancel.
                </p>
              </div>
            </div>
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
